'use client';

import { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { CATEGORY_ICON_KEY_BY_BACKEND_SLUG } from '@/features/shop/data/shop-home.mappers';

const HOME_DECORATION_ICON =
  '/assets/images/svg/categories/home-decoration.svg';

/**
 * Desktop row capacity (Figma).
 * - 3..7 cards → fill the row evenly (`slidesPerView = count`)
 * - 1..2 or >7 → fixed card width as if 7 were in the row
 */
export const DESKTOP_CATEGORY_ROW_CAPACITY = 7;
export const DESKTOP_CATEGORY_FILL_ROW_MIN_COUNT = 3;

const CATEGORY_ICON_MAP: Record<string, string> = {
  makeup: '/assets/images/svg/categories/makeup.svg',
  clothes: '/assets/images/svg/categories/clothes.svg',
  'digital-goods': '/assets/images/svg/categories/digital-goods.svg',
  'home-decoration': HOME_DECORATION_ICON,
  tools: '/assets/images/svg/categories/tools.svg',
  handicrafts: '/assets/images/svg/categories/handicrafts.svg',
  sports: '/assets/images/svg/categories/sports.svg',
  health: '/assets/images/svg/categories/health.svg',
  learning: '/assets/images/svg/categories/learning.svg',
  service: '/assets/images/svg/categories/service.svg',
  groceries: '/assets/images/svg/categories/groceries.svg',
  other: '/assets/images/svg/categories/other.svg',
};

const FALLBACK_ICON = CATEGORY_ICON_MAP.other;

export interface Category {
  id: string | number;
  title: string;
  slug: string;
}

interface CategoryCarouselProps {
  categories?: Category[];
  plpPath?: string;
  categoryQueryKey?: string;
  className?: string;
}

interface CategoryCardProps {
  category: Category;
  plpPath: string;
  categoryQueryKey: string;
}

interface ArrowButtonProps {
  direction: 'left' | 'right';
  onClick: () => void;
  hidden: boolean;
}

export function resolveCategoryIconKey(slug: string) {
  return (
    (CATEGORY_ICON_MAP[slug] ? slug : undefined) ??
    CATEGORY_ICON_KEY_BY_BACKEND_SLUG[slug] ??
    slug
  );
}

export function getCategoryIcon(slug: string) {
  const iconKey = resolveCategoryIconKey(slug);
  return CATEGORY_ICON_MAP[iconKey] ?? FALLBACK_ICON;
}

export function getDesktopCategorySlidesPerView(categoryCount: number) {
  if (categoryCount <= 0) return 1;

  if (
    categoryCount >= DESKTOP_CATEGORY_FILL_ROW_MIN_COUNT &&
    categoryCount <= DESKTOP_CATEGORY_ROW_CAPACITY
  ) {
    return categoryCount;
  }

  // 1–2 categories (and >7): keep the same card width as a full 7-card row
  return DESKTOP_CATEGORY_ROW_CAPACITY;
}

function getCategoryHref(category: Category, categoryQueryKey: string) {
  const params = new URLSearchParams({
    [categoryQueryKey]: String(category.id),
  });

  return params.toString();
}

function CategoryCard({
  category,
  plpPath,
  categoryQueryKey,
}: CategoryCardProps) {
  const icon = getCategoryIcon(category.slug);

  const query = getCategoryHref(category, categoryQueryKey);
  const href = `${plpPath}?${query}`;

  return (
    <Link
      href={href}
      prefetch={false}
      aria-label={category.title}
      className="
        group
        flex h-auto min-h-14 w-[86px] min-w-0
        flex-col items-center justify-center
        gap-1.5 rounded-[6px] bg-white
        px-2 py-2
        shadow-[0_2px_12.6px_rgba(157,157,157,0.2)]
        transition duration-200
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-neutral-400
        focus-visible:ring-offset-2

        md:h-[120px]
        md:w-full
        md:gap-3
        md:rounded-[10px]
        md:px-4
        md:py-3.5
      "
    >
      <div
        className="
          relative size-5 shrink-0
          md:size-11
        "
      >
        <Image
          src={icon}
          alt=""
          fill
          sizes="(max-width: 767px) 20px, 44px"
          unoptimized
          className="
            object-contain
            transition-transform duration-200
            group-hover:scale-105
          "
        />
      </div>

      <span
        className="
          w-full
          truncate
          text-center
          text-[10px]
          font-semibold
          leading-3
          text-black-500

          md:text-sm
          md:leading-5
        "
      >
        {category.title}
      </span>
    </Link>
  );
}
function ArrowButton({ direction, onClick, hidden }: ArrowButtonProps) {
  if (hidden) {
    return null;
  }

  const isLeft = direction === 'left';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isLeft ? 'دسته‌بندی‌های بعدی' : 'دسته‌بندی‌های قبلی'}
      className={`
        absolute top-1/2 z-50
        hidden size-10
        -translate-y-1/2
        items-center justify-center
        rounded-full
        border border-silver-default
        bg-white
        shadow-[0_2px_12.6px_rgba(157,157,157,0.2)]
        transition-colors duration-200
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-neutral-400
        focus-visible:ring-offset-2

        md:flex

        ${isLeft ? '-left-4' : '-right-4'}
      `}
    >
      {isLeft ? (
        <ArrowLeftSLineIcon size={24} />
      ) : (
        <ArrowRightSLineIcon size={24} />
      )}
    </button>
  );
}

export default function CategoryCarousel({
  categories = [],
  plpPath = '/products',
  categoryQueryKey = 'category',
  className = '',
}: CategoryCarouselProps) {
  const swiperRef = useRef<SwiperInstance | null>(null);

  const [navigationState, setNavigationState] = useState({
    isBeginning: true,
    isEnd: false,
  });

  if (!categories.length) {
    return null;
  }

  const desktopSlidesPerView = getDesktopCategorySlidesPerView(
    categories.length
  );

  const syncNavigationState = (swiper: SwiperInstance) => {
    const nextState = {
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    };

    setNavigationState((currentState) => {
      if (
        currentState.isBeginning === nextState.isBeginning &&
        currentState.isEnd === nextState.isEnd
      ) {
        return currentState;
      }

      return nextState;
    });
  };

  return (
    <div
      dir="rtl"
      className={`
      relative
      w-full min-w-0 max-w-full
      ${className}
    `}
    >
      <div className="overflow-x-auto overflow-y-visible md:hidden">
        <div
          className="
            flex w-full min-w-0 max-w-full gap-2
            [scrollbar-width:none]
            [&::-webkit-scrollbar]:hidden
          "
        >
          {categories.map((category) => (
            <div key={category.id} className="w-[86px] shrink-0">
              <CategoryCard
                category={category}
                plpPath={plpPath}
                categoryQueryKey={categoryQueryKey}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="-my-4 hidden min-w-0 overflow-hidden py-4 md:block">
        <Swiper
          dir="rtl"
          slidesPerView={desktopSlidesPerView}
          slidesPerGroup={1}
          spaceBetween={16}
          watchOverflow
          allowTouchMove={categories.length > DESKTOP_CATEGORY_ROW_CAPACITY}
          grabCursor={categories.length > DESKTOP_CATEGORY_ROW_CAPACITY}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            syncNavigationState(swiper);
          }}
          onSlideChange={syncNavigationState}
          onReachBeginning={syncNavigationState}
          onReachEnd={syncNavigationState}
          onFromEdge={syncNavigationState}
          onResize={syncNavigationState}
          onSlidesUpdated={syncNavigationState}
          className="!w-full !min-w-0 !overflow-hidden"
        >
          {categories.map((category) => (
            <SwiperSlide key={category.id} className="!h-auto">
              <CategoryCard
                category={category}
                plpPath={plpPath}
                categoryQueryKey={categoryQueryKey}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <ArrowButton
        direction="left"
        hidden={
          categories.length <= DESKTOP_CATEGORY_ROW_CAPACITY ||
          navigationState.isEnd
        }
        onClick={() => swiperRef.current?.slideNext()}
      />

      <ArrowButton
        direction="right"
        hidden={
          categories.length <= DESKTOP_CATEGORY_ROW_CAPACITY ||
          navigationState.isBeginning
        }
        onClick={() => swiperRef.current?.slidePrev()}
      />
    </div>
  );
}
